import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import axios from 'axios'
import { post } from '../api/request'
import {
  HANDOFF_KEY,
  IMPERSONATION_TOKEN_KEY,
  IMPERSONATION_SESSION_KEY,
  IMPERSONATION_ADMIN_TOKEN_KEY,
  type ImpersonationHandoff,
} from '../utils/impersonationHandoff'

interface ImpersonationSession {
  sessionId: string
  targetUser: {
    id: string
    email: string
    name: string
    type: string
  }
  expiresAt: string
}

interface ImpersonationContextType {
  session: ImpersonationSession | null
  isImpersonating: boolean
  startImpersonation: (targetUserId: string) => Promise<void>
  endImpersonation: () => Promise<void>
  timeRemaining: number | null
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null)

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<ImpersonationSession | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const stored = sessionStorage.getItem(IMPERSONATION_SESSION_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ImpersonationSession
        if (new Date(parsed.expiresAt) > new Date()) {
          setSession(parsed)
        } else {
          sessionStorage.removeItem(IMPERSONATION_SESSION_KEY)
          sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY)
          sessionStorage.removeItem(IMPERSONATION_ADMIN_TOKEN_KEY)
        }
      } catch {
        sessionStorage.removeItem(IMPERSONATION_SESSION_KEY)
      }
    }
  }, [])

  useEffect(() => {
    if (!session) {
      if (timerRef.current) clearInterval(timerRef.current)
      setTimeRemaining(null)
      return
    }

    const updateTimer = () => {
      const remaining = Math.max(
        0,
        Math.floor((new Date(session.expiresAt).getTime() - Date.now()) / 1000),
      )
      setTimeRemaining(remaining)
      if (remaining <= 0) {
        endImpersonation()
      }
    }

    updateTimer()
    timerRef.current = setInterval(updateTimer, 1000)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
     
  }, [session])

  const startImpersonation = useCallback(async (targetUserId: string) => {
    const currentToken = localStorage.getItem('token')
    if (!currentToken) throw new Error('No active token')

    const response = await post('/ui/operation/impersonation', { targetUserId })
    const data = response.data

    const handoff: ImpersonationHandoff = {
      token: data.impersonationToken,
      adminToken: currentToken,
      session: {
        sessionId: data.sessionId,
        targetUser: data.targetUser,
        expiresAt: data.expiresAt,
      },
    }

    localStorage.setItem(HANDOFF_KEY, JSON.stringify(handoff))

    window.open(`/provider/home?_imp=${data.sessionId}`, '_blank')
  }, [])

  const endImpersonation = useCallback(async () => {
    if (!session) return

    const adminToken = sessionStorage.getItem(IMPERSONATION_ADMIN_TOKEN_KEY)

    try {
      // The end-session endpoint requires SuperAdmin auth, so use the
      // original admin token directly instead of the impersonation token.
      if (adminToken) {
        await axios.post(`/ui/operation/impersonation/${session.sessionId}/end`, null, {
          headers: { Authorization: `Bearer ${adminToken}` },
        })
      }
    } catch {
      // Best-effort
    }

    sessionStorage.removeItem(IMPERSONATION_SESSION_KEY)
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY)
    sessionStorage.removeItem(IMPERSONATION_ADMIN_TOKEN_KEY)
    setSession(null)

    // This tab was opened via window.open, so window.close() works.
    // Fall back to navigating to a landing page if close is blocked.
    window.close()
    setTimeout(() => {
      window.location.href = '/login'
    }, 300)
  }, [session])

  return (
    <ImpersonationContext.Provider
      value={{
        session,
        isImpersonating: !!session,
        startImpersonation,
        endImpersonation,
        timeRemaining,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext)
  if (!ctx) {
    throw new Error('useImpersonation must be used within ImpersonationProvider')
  }
  return ctx
}
